import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import type {
  WarmupTemplate,
  WarmupItem,
  WarmupTriggerType,
  CreateWarmupTemplateInput,
} from '../services/types';
import { WarmupService } from '../services/warmupService';

const TRIGGER_TYPE_LABELS: Record<WarmupTriggerType, string> = {
  muscle_group: 'Мускулна група',
  body_region: 'Телесен регион',
  movement_pattern: 'Модел на движение',
  universal: 'Универсален',
};

const TRIGGER_VALUE_OPTIONS: Record<string, string[]> = {
  muscle_group: ['chest', 'back', 'shoulders', 'core', 'arms', 'glutes', 'quadriceps', 'hamstrings', 'adductors', 'calves', 'biceps', 'triceps'],
  body_region: ['upper_body', 'lower_body', 'full_body'],
  movement_pattern: ['push', 'pull', 'squat', 'hinge', 'carry', 'rotate'],
};

const TRIGGER_VALUE_LABELS: Record<string, string> = {
  chest: 'Гърди', back: 'Гръб', shoulders: 'Рамене', core: 'Корем',
  arms: 'Ръце', glutes: 'Седалище', quadriceps: 'Квадрицепс', hamstrings: 'Задно бедро',
  adductors: 'Аддуктори', calves: 'Прасци', biceps: 'Бицепс', triceps: 'Трицепс',
  upper_body: 'Горна половина', lower_body: 'Долна половина', full_body: 'Цяло тяло',
  push: 'Push (бутане)', pull: 'Pull (дърпане)', squat: 'Клек', hinge: 'Шарнир',
  carry: 'Носене', rotate: 'Въртене',
};

const EMPTY_ITEM: WarmupItem = { name: '', description: '', orderIndex: 0 };

const EMPTY_TEMPLATE: CreateWarmupTemplateInput = {
  name: '',
  triggerType: 'muscle_group',
  triggerValue: 'chest',
  durationEstimateMinutes: 5,
  items: [{ ...EMPTY_ITEM }],
};

interface ItemEditorProps {
  items: WarmupItem[];
  onChange: (items: WarmupItem[]) => void;
}

const ItemEditor: React.FC<ItemEditorProps> = ({ items, onChange }) => {
  const addItem = () => {
    onChange([...items, { name: '', description: '', orderIndex: items.length }]);
  };

  const updateItem = (index: number, field: keyof WarmupItem, value: string | number | undefined) => {
    const updated = items.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    onChange(updated);
  };

  const removeItem = (index: number) => {
    const updated = items
      .filter((_, i) => i !== index)
      .map((item, i) => ({ ...item, orderIndex: i }));
    onChange(updated);
  };

  return (
    <Box>
      <Stack spacing={2}>
        {items.map((item, index) => (
          <Card key={index} variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle2" color="text.secondary">
                  Стъпка {index + 1}
                </Typography>
                <IconButton size="small" color="error" onClick={() => removeItem(index)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>

              <TextField
                label="Наименование"
                value={item.name}
                onChange={(e) => updateItem(index, 'name', e.target.value)}
                size="small"
                fullWidth
                required
              />

              <TextField
                label="Описание"
                value={item.description}
                onChange={(e) => updateItem(index, 'description', e.target.value)}
                size="small"
                fullWidth
                multiline
                rows={2}
              />

              <Stack direction="row" spacing={1}>
                <TextField
                  label="Повторения"
                  type="number"
                  value={item.reps ?? ''}
                  onChange={(e) => {
                    const val = e.target.value === '' ? undefined : parseInt(e.target.value, 10);
                    updateItem(index, 'reps', val);
                  }}
                  size="small"
                  sx={{ flex: 1 }}
                  inputProps={{ min: 1 }}
                />
                <TextField
                  label="Секунди"
                  type="number"
                  value={item.durationSeconds ?? ''}
                  onChange={(e) => {
                    const val = e.target.value === '' ? undefined : parseInt(e.target.value, 10);
                    updateItem(index, 'durationSeconds', val);
                  }}
                  size="small"
                  sx={{ flex: 1 }}
                  inputProps={{ min: 1 }}
                />
              </Stack>
            </Stack>
          </Card>
        ))}
      </Stack>

      <Button
        startIcon={<AddIcon />}
        onClick={addItem}
        variant="outlined"
        size="small"
        sx={{ mt: 2 }}
      >
        Добави стъпка
      </Button>
    </Box>
  );
};

interface TemplateFormDialogProps {
  open: boolean;
  initial: CreateWarmupTemplateInput | null;
  onClose: () => void;
  onSave: (data: CreateWarmupTemplateInput) => Promise<void>;
  saving: boolean;
  error: string | null;
}

const TemplateFormDialog: React.FC<TemplateFormDialogProps> = ({
  open, initial, onClose, onSave, saving, error,
}) => {
  const [form, setForm] = useState<CreateWarmupTemplateInput>(initial ?? { ...EMPTY_TEMPLATE });

  useEffect(() => {
    setForm(initial ?? { ...EMPTY_TEMPLATE, items: [{ ...EMPTY_ITEM }] });
  }, [initial, open]);

  const setField = <K extends keyof CreateWarmupTemplateInput>(key: K, value: CreateWarmupTemplateInput[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleTriggerTypeChange = (type: WarmupTriggerType) => {
    const defaultValue = type === 'universal' ? null : (TRIGGER_VALUE_OPTIONS[type]?.[0] ?? null);
    setForm((prev) => ({ ...prev, triggerType: type, triggerValue: defaultValue }));
  };

  const handleSubmit = () => {
    onSave(form);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initial ? 'Редактирай шаблон' : 'Нов шаблон за загрявка'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label="Наименование"
            value={form.name}
            onChange={(e) => setField('name', e.target.value)}
            fullWidth
            required
            inputProps={{ maxLength: 100 }}
          />

          <FormControl fullWidth>
            <InputLabel>Тип тригер</InputLabel>
            <Select
              value={form.triggerType}
              label="Тип тригер"
              onChange={(e) => handleTriggerTypeChange(e.target.value as WarmupTriggerType)}
            >
              {Object.entries(TRIGGER_TYPE_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {form.triggerType !== 'universal' && (
            <FormControl fullWidth>
              <InputLabel>Стойност на тригера</InputLabel>
              <Select
                value={form.triggerValue ?? ''}
                label="Стойност на тригера"
                onChange={(e) => setField('triggerValue', e.target.value || null)}
              >
                {(TRIGGER_VALUE_OPTIONS[form.triggerType] ?? []).map((val) => (
                  <MenuItem key={val} value={val}>{TRIGGER_VALUE_LABELS[val] ?? val}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            label="Приблизителна продължителност (мин)"
            type="number"
            value={form.durationEstimateMinutes}
            onChange={(e) => setField('durationEstimateMinutes', Math.min(30, Math.max(1, parseInt(e.target.value, 10) || 1)))}
            fullWidth
            inputProps={{ min: 1, max: 30 }}
          />

          <Box>
            <Typography variant="subtitle2" gutterBottom>Стъпки от загрявката</Typography>
            <ItemEditor items={form.items} onChange={(items) => setField('items', items)} />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Отказ</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving || !form.name.trim() || form.items.length === 0}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          {saving ? 'Запазване…' : 'Запази'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface TemplateCardProps {
  template: WarmupTemplate;
  onEdit: () => void;
  onDelete: () => void;
}

const TemplateCard: React.FC<TemplateCardProps> = ({ template, onEdit, onDelete }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card variant="outlined">
      <CardContent sx={{ pb: '12px !important' }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {template.name}
              </Typography>
              {template.isSystem && (
                <Chip icon={<LockIcon />} label="Системен" size="small" color="default" variant="outlined" />
              )}
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
              <Chip
                label={TRIGGER_TYPE_LABELS[template.triggerType]}
                size="small"
                color="primary"
                variant="outlined"
              />
              {template.triggerValue && (
                <Chip
                  label={TRIGGER_VALUE_LABELS[template.triggerValue] ?? template.triggerValue}
                  size="small"
                />
              )}
              <Chip label={`~${template.durationEstimateMinutes} мин`} size="small" />
            </Stack>
          </Box>

          <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
            <IconButton
              size="small"
              onClick={() => setExpanded((v) => !v)}
              aria-label={expanded ? 'Скрий стъпките' : 'Покажи стъпките'}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>

            <Tooltip title={template.isSystem ? 'Системните шаблони не могат да се редактират' : 'Редактирай'}>
              <span>
                <IconButton
                  size="small"
                  onClick={onEdit}
                  disabled={template.isSystem}
                  data-testid="edit-template-btn"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title={template.isSystem ? 'Системните шаблони не могат да се изтриват' : 'Изтрий'}>
              <span>
                <IconButton
                  size="small"
                  color="error"
                  onClick={onDelete}
                  disabled={template.isSystem}
                  data-testid="delete-template-btn"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        {expanded && template.items.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Divider sx={{ mb: 1.5 }} />
            <Stack spacing={1}>
              {template.items.map((item, idx) => (
                <Box key={idx}>
                  <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                    <Typography variant="body2" fontWeight={600}>{item.name}</Typography>
                    {item.reps !== undefined && (
                      <Chip label={`${item.reps} повт.`} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                    )}
                    {item.durationSeconds !== undefined && (
                      <Chip label={`${item.durationSeconds}s`} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                    )}
                  </Stack>
                  {item.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                      {item.description}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const WarmupSettings: React.FC = () => {
  const [templates, setTemplates] = useState<WarmupTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<WarmupTemplate | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await WarmupService.listTemplates();
      setTemplates(data);
    } catch {
      setError('Грешка при зареждане на шаблоните. Опитайте отново.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const userTemplates = templates.filter((t) => !t.isSystem);
  const systemTemplates = templates.filter((t) => t.isSystem);

  const handleAddClick = () => {
    setEditingTemplate(null);
    setSaveError(null);
    setDialogOpen(true);
  };

  const handleEditClick = (template: WarmupTemplate) => {
    setEditingTemplate(template);
    setSaveError(null);
    setDialogOpen(true);
  };

  const handleSave = async (data: CreateWarmupTemplateInput) => {
    setSaving(true);
    setSaveError(null);
    try {
      if (editingTemplate) {
        const updated = await WarmupService.updateTemplate(editingTemplate.id, data);
        setTemplates((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
      } else {
        const created = await WarmupService.createTemplate(data);
        setTemplates((prev) => [created, ...prev]);
      }
      setDialogOpen(false);
    } catch (err: unknown) {
      setSaveError(
        err instanceof Error ? err.message : 'Грешка при запазване. Опитайте отново.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmId === null) return;
    setDeleting(true);
    try {
      await WarmupService.deleteTemplate(deleteConfirmId);
      setTemplates((prev) => prev.filter((t) => t.id !== deleteConfirmId));
      setDeleteConfirmId(null);
    } catch {
      setError('Грешка при изтриване. Опитайте отново.');
    } finally {
      setDeleting(false);
    }
  };

  const initialFormData = editingTemplate
    ? {
        name: editingTemplate.name,
        triggerType: editingTemplate.triggerType,
        triggerValue: editingTemplate.triggerValue,
        durationEstimateMinutes: editingTemplate.durationEstimateMinutes,
        items: editingTemplate.items,
      }
    : null;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={700}>
          Шаблони за загрявка
        </Typography>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          size="small"
          onClick={handleAddClick}
          data-testid="add-warmup-template-btn"
        >
          Добави шаблон
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={3}>
          {/* User templates */}
          <Box>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
              Мои шаблони
            </Typography>
            {userTemplates.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Нямате добавени шаблони. Натиснете „Добави шаблон", за да създадете персонализиран протокол за загрявка.
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {userTemplates.map((template) => (
                  <TemplateCard
                    key={template.id}
                    template={template}
                    onEdit={() => handleEditClick(template)}
                    onDelete={() => setDeleteConfirmId(template.id)}
                  />
                ))}
              </Stack>
            )}
          </Box>

          <Divider />

          {/* System defaults */}
          <Box>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
              Системни шаблони
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Тези шаблони се използват автоматично, когато нямате персонализиран протокол за дадена мускулна група или регион.
            </Typography>
            <Stack spacing={1.5}>
              {systemTemplates.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onEdit={() => {}}
                  onDelete={() => {}}
                />
              ))}
            </Stack>
          </Box>
        </Stack>
      )}

      {/* Add / Edit Dialog */}
      <TemplateFormDialog
        open={dialogOpen}
        initial={initialFormData}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        saving={saving}
        error={saveError}
      />

      {/* Delete confirmation dialog */}
      <Dialog open={deleteConfirmId !== null} onClose={() => setDeleteConfirmId(null)} maxWidth="xs">
        <DialogTitle>Изтриване на шаблон</DialogTitle>
        <DialogContent>
          <Typography>Сигурни ли сте, че искате да изтриете този шаблон? Действието е необратимо.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)} disabled={deleting}>Отказ</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : undefined}
          >
            {deleting ? 'Изтриване…' : 'Изтрий'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WarmupSettings;
